import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { themeColor } from '../utils/themeColor';
import {
  Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { API_BASE_URL } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { buildSymbolSummary, symbolDisclaimer, isPointsQuoted } from '../components/vectorDashboard/symbolSummary';
import { IconChevronLeft } from '../components/Icons';
import { SymbolIcon } from '../components/vectorDashboard/clientReportingMarketIcons';
import './SymbolDetailPage.css';

/**
 * Detail page for one watchlist symbol, reached by clicking a Stocks or World
 * Markets row.
 *
 * Shows only what the price feed supports: quote, session stats, day and
 * 52-week position, a price history, and a summary written from those numbers.
 * Company fundamentals — dividends, splits, market cap — are deliberately
 * absent rather than guessed at.
 */

function fmtNumber(value, { prefix = '$', points = false, decimals = 2 } = {}) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  const num = Number(value);
  const body = Math.abs(num).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  const sign = num < 0 ? '-' : '';
  return points ? `${sign}${body}` : `${sign}${prefix}${body}`;
}

function fmtSigned(value, opts) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  const formatted = fmtNumber(value, opts);
  return Number(value) >= 0 ? `+${formatted}` : formatted;
}

function fmtVolume(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return '—';
  if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return String(Math.round(num));
}

/** Low–high bar with a marker where the current price sits. */
function RangeBar({ label, low, high, position, opts }) {
  if (low === null || high === null || !Number.isFinite(Number(position))) return null;
  return (
    <div className="vd-sym-range">
      <span className="vd-sym-range-label">{label}</span>
      <div className="vd-sym-range-track">
        <span className="vd-sym-range-marker" style={{ left: `${position}%` }} />
      </div>
      <div className="vd-sym-range-ends">
        <span>{fmtNumber(low, opts)}</span>
        <span>{fmtNumber(high, opts)}</span>
      </div>
    </div>
  );
}

export default function SymbolDetailPage({ symbol, onBack }) {
  const [range, setRange] = useState('6mo');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (rangeId) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/market-data/symbol/${encodeURIComponent(symbol)}?range=${encodeURIComponent(rangeId)}`,
        { headers: getBearerAuthHeaders() },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not load this symbol');
      setDetail(data);
    } catch (e) {
      setDetail(null);
      setError(e.message || 'Could not load this symbol');
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  useEffect(() => { load(range); }, [load, range]);

  const points = isPointsQuoted(detail?.kind);
  const opts = useMemo(
    () => ({ prefix: detail?.currencyPrefix || '$', points }),
    [detail?.currencyPrefix, points],
  );

  const summary = useMemo(() => buildSymbolSummary(detail), [detail]);
  const chartData = useMemo(
    () => (detail?.series || []).map((p) => ({ t: p.t, close: p.close })),
    [detail?.series],
  );

  const changePositive = detail?.quote?.percentChange >= 0;

  if (error) {
    return (
      <div className="vd-sym-page">
        <button type="button" className="vd-sym-back" onClick={onBack}>
          <IconChevronLeft size={16} />
          Back
        </button>
        <p className="vd-sym-error">{error}</p>
      </div>
    );
  }

  return (
    <div className="vd-sym-page">
      <button type="button" className="vd-sym-back" onClick={onBack}>
        <IconChevronLeft size={16} />
        Back
      </button>

      <section className="vd-panel vd-sym-head">
        <div className="vd-sym-head-main">
          <div className="vd-sym-title-row">
            {/* The same mark the sidebar row showed — a different badge on the
                destination page reads as a different instrument. */}
            <SymbolIcon symbol={symbol} size="lg" />
            <div>
              <h1 className="vd-sym-title">
                {detail?.label || symbol}
                <span className="vd-sym-title-ticker"> ({symbol})</span>
              </h1>
              <p className="vd-sym-subtitle">
                {detail?.exchange || '—'}
                {detail?.quoteAsOf ? ` · as of ${new Date(detail.quoteAsOf).toLocaleString()}` : ''}
              </p>
            </div>
          </div>

          <div className="vd-sym-price-row">
            <span className="vd-sym-price">{fmtNumber(detail?.quote?.lastPrice, opts)}</span>
            <span className={`vd-sym-chg ${changePositive ? 'pos' : 'neg'}`}>
              {fmtSigned(detail?.quote?.netChange, opts)}
            </span>
            <span className={`vd-sym-chg ${changePositive ? 'pos' : 'neg'}`}>
              {detail?.quote?.percentChange === null || detail?.quote?.percentChange === undefined
                ? '—'
                : `${detail.quote.percentChange >= 0 ? '+' : ''}${detail.quote.percentChange.toFixed(2)}%`}
            </span>
          </div>

          <div className="vd-sym-tiles">
            <div className="vd-sym-tile">
              <span className="vd-sym-tile-value">{fmtVolume(detail?.quote?.volume)}</span>
              <span className="vd-sym-tile-label">Volume</span>
            </div>
            <div className="vd-sym-tile">
              <span className="vd-sym-tile-value">{fmtNumber(detail?.quote?.previousClose, opts)}</span>
              <span className="vd-sym-tile-label">Previous close</span>
            </div>
            <div className="vd-sym-tile">
              <span className="vd-sym-tile-value">
                {detail?.trailing?.monthPct === null || detail?.trailing?.monthPct === undefined
                  ? '—'
                  : `${detail.trailing.monthPct >= 0 ? '+' : ''}${detail.trailing.monthPct.toFixed(2)}%`}
              </span>
              <span className="vd-sym-tile-label">30 days</span>
            </div>
            <div className="vd-sym-tile">
              <span className="vd-sym-tile-value">
                {detail?.trailing?.yearPct === null || detail?.trailing?.yearPct === undefined
                  ? '—'
                  : `${detail.trailing.yearPct >= 0 ? '+' : ''}${detail.trailing.yearPct.toFixed(2)}%`}
              </span>
              <span className="vd-sym-tile-label">12 months</span>
            </div>
          </div>
        </div>

        <div className="vd-sym-ranges">
          <RangeBar
            label="Day range"
            low={detail?.quote?.dayLow}
            high={detail?.quote?.dayHigh}
            position={detail?.quote?.dayRangePosition}
            opts={opts}
          />
          <RangeBar
            label="52-week range"
            low={detail?.quote?.yearLow}
            high={detail?.quote?.yearHigh}
            position={detail?.quote?.yearRangePosition}
            opts={opts}
          />
        </div>
      </section>

      {summary.length ? (
        <section className="vd-panel vd-sym-summary">
          <h2>Summary</h2>
          {summary.map((line) => <p key={line}>{line}</p>)}
          <p className="vd-sym-disclaimer">{symbolDisclaimer(detail?.kind)}</p>
        </section>
      ) : null}

      <section className="vd-panel vd-sym-chart-panel">
        <div className="vd-sym-chart-head">
          <h2>{symbol} chart</h2>
          <div className="vd-sym-range-tabs" role="tablist" aria-label="Chart range">
            {(detail?.ranges || []).map((r) => (
              <button
                key={r.id}
                type="button"
                role="tab"
                aria-selected={range === r.id}
                className={`vd-sym-range-tab${range === r.id ? ' active' : ''}`}
                onClick={() => setRange(r.id)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <div className={`vd-sym-chart${loading ? ' is-loading' : ''}`}>
          {chartData.length > 1 ? (
            <ResponsiveContainer width="100%" height={340}>
              <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                <defs>
                  <linearGradient id="symFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={themeColor('#8b5cf6')} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={themeColor('#8b5cf6')} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="t"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(t) => new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  stroke="rgba(148,163,184,0.5)"
                  tick={{ fontSize: 11 }}
                  minTickGap={40}
                />
                <YAxis
                  domain={['auto', 'auto']}
                  stroke="rgba(148,163,184,0.5)"
                  tick={{ fontSize: 11 }}
                  width={70}
                  tickFormatter={(v) => fmtNumber(v, { ...opts, decimals: 0 })}
                />
                <Tooltip
                  contentStyle={{
                    background: 'rgba(15,23,42,0.98)',
                    border: '1px solid rgba(148,163,184,0.3)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelFormatter={(t) => new Date(t).toLocaleString()}
                  formatter={(v) => [fmtNumber(v, opts), 'Close']}
                />
                <Area
                  type="monotone"
                  dataKey="close"
                  stroke={themeColor('#8b5cf6')}
                  strokeWidth={2}
                  fill="url(#symFill)"
                  isAnimationActive={false}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="vd-sym-empty">{loading ? 'Loading…' : 'No price history available.'}</p>
          )}
        </div>
      </section>
    </div>
  );
}
